import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../../../context/AuthContext';
import {
  getSafetyCategories,
  getRiskCauses,
  getSafetyProcessMasters,
  getSafetyMasterMappings,
  getSafetyBasicInfo
} from '../../../../api/safetyAssessApi';
import SafetyMatrixModal from '../../shared/SafetyMatrixModal';

function SafetyHazardIdentify({ evalMethod = '3x3', gradeRules = [], getGradeInfo }) {
  const { token } = useContext(AuthContext);

  const [categories, setCategories] = useState([]);
  const [causes, setCauses] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [useProcess, setUseProcess] = useState(true);

  const [selectedProcessId, setSelectedProcessId] = useState(null);
  // 프로젝트 개별 매핑 데이터 (초기에는 마스터 데이터를 복사해서 사용)
  const [mappings, setMappings] = useState([]);
  const [tempMappings, setTempMappings] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedCatId, setExpandedCatId] = useState(null);
  const [showMatrixModal, setShowMatrixModal] = useState(false);
  const [showMatrixGuide, setShowMatrixGuide] = useState(false);

  // 편집 모드 상태 (안전관리자가 "위험요인 변경" 클릭 시 true)
  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    if (token) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    try {
      const [catData, causeData, procData, mapData, basicInfo] = await Promise.all([
        getSafetyCategories(token),
        getRiskCauses('', token),
        getSafetyProcessMasters(token),
        getSafetyMasterMappings(token),
        getSafetyBasicInfo(token)
      ]);
      const colorMap = {
        1: { main: '#7c3aed', sub: '#a78bfa' },
        2: { main: '#f59e0b', sub: '#fbbf24' },
        3: { main: '#059669', sub: '#34d399' },
        4: { main: '#dc2626', sub: '#f87171' },
        5: { main: '#2563eb', sub: '#60a5fa' },
        6: { main: '#0891b2', sub: '#22d3ee' },
        7: { main: '#db2777', sub: '#f472b6' },
        8: { main: '#65a30d', sub: '#a3e635' },
        9: { main: '#475569', sub: '#94a3b8' },
        10: { main: '#9333ea', sub: '#c084fc' },
      };
      const enrichedCategories = (catData || []).map((c, i) => {
        const cColors = colorMap[c.id] || colorMap[(i % 10) + 1];
        return { ...c, color: cColors.main, subColor: cColors.sub };
      });
      setCategories(enrichedCategories);
      setCauses(causeData || []);
      setProcesses(procData || []);
      setMappings(mapData || []);

      if (basicInfo && basicInfo.useProcess === false) {
        setUseProcess(false);
        setSelectedProcessId(null);
      } else if (procData && procData.length > 0) {
        setUseProcess(true);
        setSelectedProcessId(procData[0].id);
      }
    } catch (error) {
      console.error('Step2 데이터 로드 실패:', error);
    }
  };

  const selectedProcess = processes.find(p => p.id === selectedProcessId);

  const selectedCauses = mappings
    .filter(m => m.processId === selectedProcessId)
    .map(m => {
      const cause = causes.find(c => c.id === m.causeId);
      const cat = categories.find(ct => ct.id === cause?.categoryId);
      return { ...cause, categoryName: cat?.name, categoryColor: cat?.color };
    })
    .filter(Boolean);

  const handleOpenAddModal = () => {
    setTempMappings([...mappings]);
    setExpandedCatId(categories.length > 0 ? categories[0].id : null);
    setShowAddModal(true);
  };

  const handleTempAddCause = (causeId) => {
    const exists = tempMappings.some(m => m.processId === selectedProcessId && m.causeId === causeId);
    if (!exists) setTempMappings(prev => [...prev, { processId: selectedProcessId, causeId }]);
  };

  const handleTempRemoveCause = (causeId) => {
    setTempMappings(prev => prev.filter(m => !(m.processId === selectedProcessId && m.causeId === causeId)));
  };

  const handleApplyModal = () => {
    setMappings(tempMappings);
    setShowAddModal(false);
  };

  const handleRemoveCause = (causeId) => {
    if (!isEditable) return;
    setMappings(prev => prev.filter(m => !(m.processId === selectedProcessId && m.causeId === causeId)));
  };

  const toggleEditMode = () => {
    if (isEditable) {
      alert('프로젝트 개별 위험요인 설정이 저장되었습니다.');
    }
    setIsEditable(!isEditable);
  };

  const getCauseCount = (processId) => mappings.filter(m => m.processId === processId).length;

  const isCheckedInMatrix = (processId, causeId) => mappings.some(m => m.processId === processId && m.causeId === causeId);

  return (
    <div className="sf-card sf-animate">
      <div className="sf-card-header" style={{ background: '#2b2a6b', color: '#ffffff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h5 className="sf-card-header-title" style={{ color: '#ffffff', margin: 0 }}><i className="bi bi-shield-check me-2"></i> 1. 유해·위험요인 파악</h5>
        <div className="d-flex align-items-center gap-2">
          <button
            className="sf-btn sf-btn-sm"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600, height: '31px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowMatrixModal(true)}
          >
            <i className="bi bi-grid-3x3 me-1"></i> 전체 매트릭스 확인
          </button>
          <button
            className="sf-btn sf-btn-sm"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600, height: '31px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowMatrixGuide(true)}
          >
            <i className="bi bi-grid-3x3-gap me-1"></i> 평가 기준표
          </button>
          {useProcess && (
            <button
              className={`sf-btn ${isEditable ? 'sf-btn-primary' : 'sf-btn-white'} sf-btn-sm`}
              style={{ fontWeight: 700, border: '1px solid transparent', height: '31px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={toggleEditMode}
            >
              {isEditable ? '💾 변경사항 저장' : '✏️ 위험요인 변경'}
            </button>
          )}
        </div>
      </div>
      <div className="sf-card-body">
        <div className="row g-4">
          {/* ────── 좌측: 공정 리스트 (공정 사용 시에만 표출) ────── */}
          {useProcess && (
            <div className="col-lg-3">
              <div className="sf-card h-100">
                <div className="sf-card-header sf-gradient-primary" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', height: '53px' }}>
                  <h5 className="sf-card-header-title" style={{ fontSize: '0.9rem', margin: 0, color: '#fff' }}>평가 대상 공정</h5>
                </div>
                <div className="sf-card-body" style={{ padding: 0 }}>
                  {processes.length === 0 ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                      <i className="bi bi-folder-x" style={{ fontSize: '2.5rem', color: '#cbd5e1' }}></i>
                      <div style={{ marginTop: 16, fontSize: '0.85rem', color: '#64748b', fontWeight: 600, wordBreak: 'keep-all' }}>
                        대상 공정이 없습니다.
                      </div>
                    </div>
                  ) : (
                    processes.map(proc => {
                      const isActive = selectedProcessId === proc.id;
                      const count = getCauseCount(proc.id);
                      return (
                        <div
                          key={proc.id}
                          onClick={() => setSelectedProcessId(proc.id)}
                          style={{
                            padding: '14px 16px', cursor: 'pointer', borderBottom: '1px solid var(--sf-border)',
                            background: isActive ? 'rgba(99,102,241,0.08)' : '#fff',
                            borderLeft: isActive ? '4px solid var(--sf-primary)' : '4px solid transparent',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                            <div style={{ fontWeight: 700, fontSize: '0.85rem', color: isActive ? 'var(--sf-primary)' : '#1e293b', wordBreak: 'keep-all', lineHeight: '1.4' }}>
                              {proc.name}
                            </div>
                            <span style={{
                              flexShrink: 0, whiteSpace: 'nowrap',
                              fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12,
                              background: count > 0 ? '#e0e7ff' : '#fee2e2', color: count > 0 ? '#4338ca' : '#dc2626'
                            }}>{count > 0 ? `${count}건` : '0건'}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ────── 우측: 선택 공정의 위험요인 태그 (공정 미사용 시 12칸 전체 확장) ────── */}
          <div className={useProcess ? "col-lg-9" : "col-lg-12"}>
            <div className="sf-card h-100" style={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              {!useProcess ? (
                <>
                  <div className="sf-card-header sf-gradient-accent" style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '53px' }}>
                    <h5 className="sf-card-header-title" style={{ fontSize: '0.9rem', margin: 0, color: '#fff' }}>
                      <i className="bi bi-exclamation-triangle me-2"></i>사업장 공통의 유해·위험요인
                    </h5>
                  </div>
                  <div className="sf-card-body">
                    <div>
                      {categories.map(cat => {
                        const catCauses = causes.filter(c => c.categoryId === cat.id);
                        return (
                          <div key={cat.id} style={{ marginBottom: 18 }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, padding: '7px 12px',
                              borderRadius: 8, background: `${cat.color}12`, borderLeft: `4px solid ${cat.color}`,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                            }}>
                              <span style={{ fontWeight: 900, fontSize: '0.88rem', color: '#1e293b' }}>{cat.name}</span>
                              <span style={{
                                fontSize: '0.7rem', color: '#fff', background: cat.color, borderRadius: 10,
                                padding: '2px 8px', fontWeight: 700, lineHeight: '17px'
                              }}>{catCauses.length}건</span>
                            </div>
                            {catCauses.length === 0 ? (
                              <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem', background: '#fff', borderRadius: 8, border: '1px dashed #cbd5e1', marginLeft: 20 }}>
                                - 등록된 유해·위험요인이 없습니다 -
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 4px 4px 20px' }}>
                                {catCauses.map(cause => (
                                  <div key={cause.id} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px 4px 7px', borderRadius: 20,
                                    background: `${cat.subColor}18`, fontSize: '0.82rem', fontWeight: 600, color: cat.color,
                                    border: `1px solid ${cat.subColor}40`, cursor: 'default', transition: 'all 0.15s'
                                  }}>
                                    <span style={{
                                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                      background: cat.color, flexShrink: 0
                                    }}></span>
                                    <span style={{ color: '#334155', fontWeight: 500 }}>{cause.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : !selectedProcess ? (
                <div className="sf-card-body d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                  <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                    <i className="bi bi-arrow-left-circle" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}></i>
                    <h5 style={{ fontWeight: 700 }}>좌측에서 공정을 선택해 주세요</h5>
                  </div>
                </div>
              ) : (
                <>
                  <div className="sf-card-header sf-gradient-accent" style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '53px' }}>
                    <h5 className="sf-card-header-title" style={{ fontSize: '0.9rem', margin: 0 }}>
                      <i className="bi bi-exclamation-triangle me-2"></i>"{selectedProcess?.name}"의 유해·위험요인
                    </h5>
                    {isEditable && (
                      <button className="sf-btn sf-btn-white sf-btn-sm" style={{ fontWeight: 700 }} onClick={handleOpenAddModal}>
                        <i className="bi bi-gear-fill me-1"></i> 위험원인 설정
                      </button>
                    )}
                  </div>
                  <div className="sf-card-body">
                    <div>
                      {categories.map(cat => {
                        const catCauses = selectedCauses.filter(c => c.categoryId === cat.id);
                        return (
                          <div key={cat.id} style={{ marginBottom: 18 }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, padding: '7px 12px',
                              borderRadius: 8, background: `${cat.color}12`, borderLeft: `4px solid ${cat.color}`,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                            }}>
                              <span style={{ fontWeight: 900, fontSize: '0.88rem', color: '#1e293b' }}>{cat.name}</span>
                              <span style={{
                                fontSize: '0.7rem', color: '#fff', background: cat.color, borderRadius: 10,
                                padding: '2px 8px', fontWeight: 700, lineHeight: '17px'
                              }}>{catCauses.length}건</span>
                            </div>
                            {catCauses.length === 0 ? (
                              <div style={{ padding: '10px', textAlign: 'center', color: '#94a3b8', fontSize: '0.78rem', background: '#fff', borderRadius: 8, border: '1px dashed #cbd5e1', marginLeft: 20 }}>
                                - 해당 공정에 매핑된 유해·위험요인이 없습니다 -
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 4px 4px 20px' }}>
                                {catCauses.map(cause => (
                                  <div key={cause.id} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px 4px 7px', borderRadius: 20,
                                    background: `${cat.subColor}18`, fontSize: '0.82rem', fontWeight: 600, color: cat.color,
                                    border: `1px solid ${cat.subColor}40`, cursor: 'default', transition: 'all 0.15s'
                                  }}>
                                    <span style={{
                                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                      background: cat.color, flexShrink: 0
                                    }}></span>
                                    <span style={{ color: '#334155', fontWeight: 500 }}>{cause.name}</span>
                                    {isEditable && (
                                      <button onClick={() => handleRemoveCause(cause.id)} style={{
                                        border: 'none', background: 'transparent', cursor: 'pointer', color: '#cbd5e1',
                                        padding: 0, marginLeft: 2, fontSize: '0.75rem', lineHeight: 1, transition: 'color 0.15s'
                                      }} title="제거" onMouseOver={e => e.currentTarget.style.color = '#ef4444'} onMouseOut={e => e.currentTarget.style.color = '#cbd5e1'}>
                                        <i className="bi bi-x-circle-fill"></i>
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ────── 위험요인 추가 모달 (편집 모드 시) ────── */}
        {showAddModal && isEditable && createPortal(
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }} onClick={() => setShowAddModal(false)}>
            <div style={{
              background: '#fff', width: '600px', maxHeight: '85vh', borderRadius: 16, display: 'flex', flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '20px 24px', background: 'var(--sf-gradient-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', marginBottom: 4 }}>{selectedProcess?.name}</div>
                  <h5 style={{ color: '#fff', fontWeight: 800, margin: 0 }}>위험요인 선택 (마스터 불러오기)</h5>
                </div>
                <button onClick={() => setShowAddModal(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
              </div>

              <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                {categories.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8' }}>
                    등록된 대분류가 없습니다.
                  </div>
                ) : (
                  categories.map(cat => {
                    const isExpanded = expandedCatId === cat.id;
                    const catCauses = causes.filter(c => c.categoryId === cat.id);
                    return (
                      <div key={cat.id} style={{ marginBottom: 12, border: `1px solid ${isExpanded ? cat.color : 'var(--sf-border)'}`, borderRadius: 12, overflow: 'hidden', transition: 'all 0.2s' }}>
                        <div
                          onClick={() => setExpandedCatId(isExpanded ? null : cat.id)}
                          style={{ padding: '14px 20px', background: isExpanded ? `${cat.color}08` : '#f8fafc', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: cat.color }}></span>
                            <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#1e293b' }}>{cat.name}</span>
                          </div>
                        </div>
                        {isExpanded && (
                          <div style={{ padding: '8px 12px', background: '#fff' }}>
                            {catCauses.map(cause => {
                              const alreadyAdded = tempMappings.some(m => m.processId === selectedProcessId && m.causeId === cause.id);
                              return (
                                <div key={cause.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', fontSize: '0.85rem', borderBottom: '1px dashed #e2e8f0', color: '#475569' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <span style={{ fontWeight: 800, color: 'var(--sf-primary)', fontSize: '0.75rem' }}>{cause.code}</span>
                                    <span>{cause.name}</span>
                                  </div>
                                  {alreadyAdded ? (
                                    <button onClick={() => handleTempRemoveCause(cause.id)} style={{ border: 'none', background: '#dcfce7', color: '#166534', fontSize: '0.75rem', fontWeight: 700, padding: '4px 12px', borderRadius: 12, cursor: 'pointer', flexShrink: 0 }}>✓ 추가됨</button>
                                  ) : (
                                    <button onClick={() => handleTempAddCause(cause.id)} style={{ border: '1px solid var(--sf-primary)', background: '#fff', color: 'var(--sf-primary)', fontSize: '0.75rem', fontWeight: 700, padding: '3px 12px', borderRadius: 12, cursor: 'pointer', flexShrink: 0 }}>+ 추가</button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="sf-btn sf-btn-primary" onClick={handleApplyModal} style={{ padding: '8px 24px', fontWeight: 700 }}>선택 완료</button>
              </div>
            </div>
          </div>, document.body
        )}
        {showMatrixGuide && createPortal(
          <div
            style={{
              position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
              backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)',
              zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
            }}
            onClick={() => setShowMatrixGuide(false)}
          >
            <div
              style={{
                position: 'relative', width: '100%', maxWidth: evalMethod === '3x3' ? '440px' : '640px',
                background: '#fff', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                overflow: 'hidden', display: 'flex', flexDirection: 'column'
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', color: '#fff', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h5 style={{ margin: 0, fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}><i className="bi bi-grid-3x3-gap me-2"></i>위험성평가 기준표 ({evalMethod === '3x3' ? '3x3 행렬' : '5x5 행렬'})</h5>
                <button onClick={() => setShowMatrixGuide(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
              </div>

              <div style={{ padding: '24px', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* 상단 안내 */}
                <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h6 style={{ fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>위험성 등급 산정 기준</h6>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {gradeRules.map((rule, idx) => (
                      <span key={idx} className="sf-badge" style={{ background: `${rule.color}15`, color: rule.color, border: `1px solid ${rule.color}30`, fontSize: '0.8rem', padding: '4px 10px', fontWeight: 700 }}>
                        {rule.min}~{rule.max}점: {rule.grade}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 매트릭스 표 */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', background: '#fff', padding: '24px 20px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', overflowX: 'auto', width: '100%' }}>
                  {(() => {
                    const size = evalMethod === '3x3' ? 3 : 5;
                    const arr = Array.from({ length: size }, (_, i) => i + 1);
                    const rArr = [...arr].reverse();

                    const colLabels3x3 = { 1: '1점 (경미)', 2: '2점 (보통)', 3: '3점 (자주)' };
                    const rowLabels3x3 = { 3: '3점 (중대)', 2: '2점 (보통)', 1: '1점 (경미)' };

                    const colLabels5x5 = { 1: '1(거의 없음)', 2: '2(드물게)', 3: '3(가끔)', 4: '4(자주)', 5: '5(지속적)' };
                    const rowLabels5x5 = { 5: '5(치명적)', 4: '4(중대)', 3: '3(보통)', 2: '2(경미)', 1: '1(무시가능)' };

                    return (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${size + 1}, 1fr)`, gap: '6px', background: '#fff', textAlign: 'center', fontSize: size === 3 ? '0.78rem' : '0.72rem', fontWeight: 800, width: '100%', maxWidth: size === 3 ? '380px' : '580px' }}>
                          {/* 헤더 좌상단 코너 */}
                          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', minHeight: size === 3 ? '38px' : '46px', height: 'auto', padding: size === 3 ? '4px 2px' : '6px 4px', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>강도 \ 빈도</div>
                          {/* 열 라벨 (빈도) */}
                          {arr.map(c => (
                            <div key={`hc${c}`} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', minHeight: size === 3 ? '38px' : '46px', height: 'auto', padding: size === 3 ? '4px 2px' : '6px 4px', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {size === 3 ? colLabels3x3[c] : colLabels5x5[c]}
                            </div>
                          ))}
                          {/* 행 라벨 및 셀 데이터 */}
                          {rArr.map(r => (
                            <React.Fragment key={`r${r}`}>
                              {/* 행 라벨 (강도) */}
                              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', minHeight: size === 3 ? '38px' : '46px', height: 'auto', padding: size === 3 ? '4px 2px' : '6px 4px', color: '#475569', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {size === 3 ? rowLabels3x3[r] : rowLabels5x5[r]}
                              </div>
                              {/* 셀 */}
                              {arr.map(c => {
                                const score = r * c;
                                const { grade, color } = getGradeInfo(score);
                                const cleanGrade = grade ? grade.replace(/\(\d+단계\)/g, '') : '';
                                return (
                                  <div key={`c${c}`} style={{ background: color, color: '#fff', border: `1px solid ${color}`, borderRadius: '8px', minHeight: size === 3 ? '38px' : '46px', height: 'auto', padding: size === 3 ? '4px 2px' : '6px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', textShadow: '0 1px 2px rgba(0,0,0,0.15)', fontSize: size === 3 ? '0.8rem' : '0.8rem', fontWeight: 900, whiteSpace: 'nowrap' }}>
                                    {size === 3 ? `${score}점(${cleanGrade})` : `${score}점`}
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </div>

                        {/* 하단 가이드 문구 */}
                        <div style={{ width: '100%', maxWidth: size === 3 ? '380px' : '580px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', textAlign: 'center', fontSize: '0.825rem', color: '#475569', fontWeight: 600 }}>
                          위험성 점수 = <span style={{ color: '#2563eb', fontWeight: 800 }}>빈도</span> × <span style={{ color: '#ef4444', fontWeight: 800 }}>강도</span> (최소 1점 ~ 최대 {size === 3 ? 9 : 25}점)
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>, document.body
        )}
        <SafetyMatrixModal
          show={showMatrixModal}
          onClose={() => setShowMatrixModal(false)}
          categories={categories}
          causes={causes}
          processes={processes}
          isCheckedInMatrix={isCheckedInMatrix}
          useProcess={useProcess}
        />
      </div>
    </div>
  );
}

export default SafetyHazardIdentify;
